<template>
  <div class="space-y-8 pb-12">
    <!-- Header de bienvenue -->
    <div class="relative overflow-hidden rounded-3xl bg-primary p-8 sm:p-10 text-white shadow-2xl shadow-primary/20">
      <div class="absolute -right-10 -top-10 size-64 rounded-full bg-white/5 blur-3xl"></div>
      <div class="absolute right-20 bottom-0 size-40 rounded-full bg-white/5 blur-2xl"></div>
      
      <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div class="space-y-2">
          <h1 class="text-3xl sm:text-4xl font-black tracking-tight">
            Ravi de vous revoir, {{ auth.user?.name || 'Client' }} 👋
          </h1>
          <p class="text-white/70 font-medium text-lg max-w-xl">
            Votre plateforme de réexpédition Guépard Express est à jour. Voici l'état de vos activités aujourd'hui.
          </p>
        </div>
        <div class="flex items-center gap-3">
          <RouterLink
            to="/app/commandes"
            class="h-14 px-8 rounded-2xl bg-white text-primary font-black flex items-center justify-center shadow-xl hover:scale-105 transition-transform"
          >
            Nouveau colis
          </RouterLink>
        </div>
      </div>
    </div>

    <!-- Grille de statistiques -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div class="stat-card">
        <div class="flex items-center justify-between mb-4">
          <div class="stat-icon bg-blue-50 text-blue-600">
            <span class="material-symbols-outlined">inventory_2</span>
          </div>
          <span class="text-[10px] font-black uppercase tracking-widest text-slate-400">Total</span>
        </div>
        <div class="text-3xl font-black text-slate-900">{{ stats.total || 0 }}</div>
        <div class="text-sm font-bold text-slate-500 mt-1">Colis enregistrés</div>
      </div>

      <div class="stat-card">
        <div class="flex items-center justify-between mb-4">
          <div class="stat-icon bg-amber-50 text-amber-600">
            <span class="material-symbols-outlined">pending_actions</span>
          </div>
          <span class="text-[10px] font-black uppercase tracking-widest text-slate-400">En attente</span>
        </div>
        <div class="text-3xl font-black text-slate-900">{{ stats.pending || 0 }}</div>
        <div class="text-sm font-bold text-slate-500 mt-1">À traiter</div>
      </div>

      <div class="stat-card">
        <div class="flex items-center justify-between mb-4">
          <div class="stat-icon bg-indigo-50 text-indigo-600">
            <span class="material-symbols-outlined">local_shipping</span>
          </div>
          <span class="text-[10px] font-black uppercase tracking-widest text-slate-400">Transit</span>
        </div>
        <div class="text-3xl font-black text-slate-900">{{ stats.transit || 0 }}</div>
        <div class="text-sm font-bold text-slate-500 mt-1">Expéditions en route</div>
      </div>

      <div class="stat-card border-primary/20 bg-primary/5">
        <div class="flex items-center justify-between mb-4">
          <div class="stat-icon bg-primary text-white">
            <span class="material-symbols-outlined">account_balance_wallet</span>
          </div>
          <span class="text-[10px] font-black uppercase tracking-widest text-primary">Solde</span>
        </div>
        <div class="text-3xl font-black text-primary">{{ formatPrice(stats.wallet || 0) }}</div>
        <div class="text-sm font-bold text-primary/60 mt-1">Portefeuille GX</div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <!-- Actions rapides -->
      <div class="lg:col-span-2 space-y-6">
        <div class="rounded-3xl bg-white border border-primary/10 p-8">
          <div class="flex items-center justify-between mb-8">
            <h2 class="text-xl font-black text-slate-900 flex items-center gap-3">
              <span class="material-symbols-outlined text-primary">bolt</span>
              Actions rapides
            </h2>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <RouterLink to="/app/commandes" class="action-btn group">
              <div class="size-12 rounded-2xl bg-slate-50 text-slate-400 group-hover:bg-primary group-hover:text-white transition-all flex items-center justify-center">
                <span class="material-symbols-outlined">add_box</span>
              </div>
              <div class="text-left">
                <div class="font-black text-slate-900">Déclarer un achat</div>
                <div class="text-xs font-bold text-slate-500">Annoncer l'arrivée d'un colis</div>
              </div>
            </RouterLink>
            <RouterLink to="/app/commandes" class="action-btn group">
              <div class="size-12 rounded-2xl bg-slate-50 text-slate-400 group-hover:bg-primary group-hover:text-white transition-all flex items-center justify-center">
                <span class="material-symbols-outlined">compare_arrows</span>
              </div>
              <div class="text-left">
                <div class="font-black text-slate-900">Réexpédier</div>
                <div class="text-xs font-bold text-slate-500">Envoyer vos colis stockés</div>
              </div>
            </RouterLink>
            <RouterLink to="/app/shipments" class="action-btn group">
              <div class="size-12 rounded-2xl bg-slate-50 text-slate-400 group-hover:bg-primary group-hover:text-white transition-all flex items-center justify-center">
                <span class="material-symbols-outlined">payments</span>
              </div>
              <div class="text-left">
                <div class="font-black text-slate-900">Mes expéditions</div>
                <div class="text-xs font-bold text-slate-500">Gérer vos envois</div>
              </div>
            </RouterLink>
            <button class="action-btn group" @click="contactSupport">
              <div class="size-12 rounded-2xl bg-slate-50 text-slate-400 group-hover:bg-primary group-hover:text-white transition-all flex items-center justify-center">
                <span class="material-symbols-outlined">support_agent</span>
              </div>
              <div class="text-left">
                <div class="font-black text-slate-900">Support</div>
                <div class="text-xs font-bold text-slate-500">Aide et réclamations</div>
              </div>
            </button>
          </div>
        </div>

        <!-- Derniers mouvements -->
        <div class="rounded-3xl bg-white border border-primary/10 p-8">
          <h2 class="text-xl font-black text-slate-900 mb-6">Dernières activités</h2>
          <div class="space-y-4">
            <div v-if="loading" class="py-10 text-center text-slate-400 font-bold">Chargement...</div>
            <template v-else-if="recentActivities.length">
              <div v-for="activity in recentActivities" :key="activity.id" class="flex items-center justify-between p-4 rounded-2xl border border-primary/5 hover:bg-slate-50 transition-colors">
                <div class="flex items-center gap-4">
                  <div class="size-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                    <span class="material-symbols-outlined text-sm">history</span>
                  </div>
                  <div>
                    <div class="text-sm font-black text-slate-800">{{ activity.message }}</div>
                    <div class="text-xs font-bold text-slate-400">{{ formatDate(activity.date) }}</div>
                  </div>
                </div>
                <RouterLink :to="activity.link" class="text-xs font-black text-primary">{{ activity.actionLabel }}</RouterLink>
              </div>
            </template>
            <div v-else class="py-10 text-center text-slate-400 font-bold">Aucune activité récente</div>
          </div>
        </div>
      </div>

      <!-- Sidebar droite Dashboard -->
      <div class="space-y-6">
        <div class="rounded-3xl bg-white border border-primary/10 p-8">
          <h3 class="font-black text-slate-900 mb-4">Votre entrepôt</h3>
          <div class="aspect-video rounded-2xl bg-slate-100 mb-4 flex items-center justify-center text-slate-300">
            <span class="material-symbols-outlined text-4xl">map</span>
          </div>
          <div class="space-y-4">
            <div>
              <div class="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Adresse de livraison</div>
              <div class="text-sm font-bold text-slate-800 leading-relaxed">
                12 Rue de l'Industrie,<br>
                93000 Bobigny, France
              </div>
            </div>
            <button class="w-full h-11 rounded-xl border border-primary/20 text-primary font-bold text-sm hover:bg-primary/5 transition-colors">
              Copier l'adresse
            </button>
          </div>
        </div>

        <div class="rounded-3xl bg-slate-900 p-8 text-white relative overflow-hidden">
          <div class="absolute -right-4 -bottom-4 size-24 bg-primary/20 rounded-full blur-2xl"></div>
          <h3 class="font-black mb-2">Besoin d'aide ?</h3>
          <p class="text-white/60 text-sm font-medium mb-6">Notre équipe est disponible 7j/7 pour vous accompagner.</p>
          <button class="w-full h-11 rounded-xl bg-white text-slate-900 font-black text-sm">
            Contacter le support
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth'
import axios from 'axios'

export default {
  name: 'DashboardPage',
  data() {
    return {
      loading: true,
      stats: {
        total: 0,
        pending: 0,
        transit: 0,
        wallet: 0
      },
      recentActivities: []
    }
  },
  computed: {
    auth() {
      return useAuthStore()
    }
  },
  async mounted() {
    await this.fetchDashboardData()
  },
  methods: {
    async fetchDashboardData() {
      this.loading = true
      try {
        // On récupère les commandes ET les expéditions pour un dashboard complet
        const [ordersRes, shipmentsRes] = await Promise.all([
          axios.get('/commandes'),
          axios.get('/ugo/shipments')
        ])
        
        const ordersPayload = ordersRes.data
        const shipmentsPayload = shipmentsRes.data
        
        if (ordersPayload.success) {
          const allOrders = ordersPayload.data?.data || ordersPayload.data || []
          this.stats.total = allOrders.length
          this.stats.pending = allOrders.filter(o => o.statut === 0 || o.statut === 1).length
          
          // Activités récentes basées sur les commandes
          this.recentActivities = allOrders.slice(0, 5).map(o => {
            const hasShipment = !!(o.shipment || o.commande?.shipment);
            return {
              id: o.id,
              message: `Commande #${o.num_facture || o.id} - ${o.name}`,
              date: o.date_creation || new Date().toISOString(),
              link: `/app/commandes/${o.id}`,
              actionLabel: hasShipment ? 'Suivre' : 'Détails'
            };
          })
        }

        if (shipmentsPayload.success) {
          const allShipments = shipmentsPayload.shipments || []
          // Transit = Expéditions avec statut 'shipped' ou 'in_transit'
          this.stats.transit = allShipments.filter(s => s.status === 'shipped' || s.status === 'in_transit').length
        }
      } catch (e) {
        console.error('Erreur chargement dashboard:', e)
      } finally {
        this.loading = false
      }
    },
    formatPrice(val) {
      // Conversion automatique CFA -> EURO (Taux fixe : 655.957)
      const euroValue = val / 655.957
      
      const cfaFormatter = new Intl.NumberFormat('fr-FR', { 
        style: 'currency', 
        currency: 'XOF',
        maximumFractionDigits: 0 
      })

      const euroFormatter = new Intl.NumberFormat('fr-FR', { 
        style: 'currency', 
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })

      return `${cfaFormatter.format(val)} (${euroFormatter.format(euroValue)})`
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
    },
    contactSupport() {
      // Logique support
      alert('Support bientôt disponible')
    }
  }
}
</script>

<style scoped>
.stat-card {
  @apply rounded-3xl bg-white border border-primary/10 p-6 shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all;
}
.stat-icon {
  @apply size-12 rounded-2xl flex items-center justify-center;
}
.action-btn {
  @apply flex items-center gap-4 p-4 rounded-2xl border border-primary/10 hover:border-primary hover:bg-primary/5 transition-all;
}
</style>
